/** Roles that grant full, unscoped visibility. Mirrors auth/roleUtils.isAdmin. */
const ADMIN_ROLE = 'BRG.Admin';

/**
 * Builds the "Access scope" line stamped into every export.
 *
 * Admins export everything; everyone else is scoped to the BRGs they are
 * assigned to, so the file states which BRGs it actually covers.
 */
export function buildScopeLabel(
  roles: string[] | undefined,
  assignedBrgNames: string[]
): string {
  if ((roles ?? []).includes(ADMIN_ROLE)) {
    return 'Admin — all BRGs';
  }
  if (assignedBrgNames.length === 0) {
    return 'Lead — no assigned BRGs';
  }
  return `Lead — ${[...assignedBrgNames].sort((a, b) => a.localeCompare(b)).join(', ')}`;
}

export function isAdminRole(roles: string[] | undefined): boolean {
  return (roles ?? []).includes(ADMIN_ROLE);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * `BRG-Members_2026-09-18_1432` — sortable, unambiguous, and safe on Windows.
 */
export function buildExportFileName(base: string, now: Date = new Date()): string {
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(
    now.getHours()
  )}${pad(now.getMinutes())}`;
  return `${base.replace(/[\\/:*?"<>|]/g, '-')}_${stamp}`;
}

export function formatExportTimestamp(now: Date = new Date()): string {
  return now.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * ISO or date-like string → 'MM/DD/YYYY'. Empty input yields an em dash.
 *
 * Bare `YYYY-MM-DD` values (the filter dates and scheduled dates the API
 * returns) are calendar dates, not instants — `new Date('2026-01-01')` parses
 * them as UTC midnight, which renders as the previous day west of Greenwich.
 * Those are built in local time instead; full timestamps keep instant
 * semantics.
 */
export function formatExportDate(value: string | null | undefined): string {
  if (!value) return '—';

  const dateOnly = DATE_ONLY.exec(value);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);

  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/** Triggers a browser download for a generated file. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
